import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Routes, Route, Link, NavLink } from 'react-router-dom';
import { 
  LayoutDashboard, Calendar, Scissors, Settings, 
  LogOut, Menu, X, User, Bell, CreditCard,
  ChevronRight, TrendingUp, Users, Clock, MessageCircle,
  Filter, ImagePlus, Sparkles, Ban
} from 'lucide-react';
import SkeletonLoading, { SkeletonStatsCard, SkeletonAppointmentCard } from '../components/SkeletonLoading';
import { getAppointments, getBarbershop, getBarbershopServices, cancelAppointment } from '../utils/api';
import api from '../utils/api';
import { formatDate, formatDateDisplay, formatTime, statusColors, statusLabels, cn, isNonTerminalStatus } from '../utils/helpers';
import WhatsAppConfig from './WhatsAppConfig';
import MercadoPagoConfig from './MercadoPagoConfig';
import ServicesManager from './ServicesManager';
import ProfessionalDashboard from './ProfessionalDashboard';
import BarbersManager from './BarbersManager';
import SettingsPage from './Settings';
import StoriesManager from './StoriesManager';
import OnboardingPage from './OnboardingPage';
import CustomerPlansManager from './CustomerPlansManager';
import { EmptyState, InlineAlert, LoadingState, PageHeader, SectionCard } from '../components/ui';
import { useToast } from '../components/ToastProvider';
import { useTutorial } from '../tutorial/TutorialProvider';
import OnboardingChecklist from '../components/OnboardingChecklist';

const getImageUrl = (url) => {
  if (!url) return null;
  return url.startsWith('http') ? url : `${import.meta.env.VITE_API_URL || 'http://localhost:3001'}${url}`;
};

// Sub-components
function DashboardHome({ barbershop, setBarbershopName, onRestartTutorial }) {
  const { id } = useParams();
  const toast = useToast();
  const [stats, setStats] = useState(null);
  const [todayAppointments, setTodayAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusLoading, setStatusLoading] = useState({});
  const [barbers, setBarbers] = useState([]);
  const [selectedBarber, setSelectedBarber] = useState('todos');
  const [lastUpdate, setLastUpdate] = useState(new Date());
  const [dateFilter, setDateFilter] = useState('hoje'); // 'hoje' or 'mes'
  const [whatsappStatus, setWhatsappStatus] = useState(null);
  const [servicesCount, setServicesCount] = useState(0);
  const tutorial = useTutorial();

  useEffect(() => {
    loadData();
    loadBarbers();
    loadWhatsappStatus();
    loadServicesCount();
  }, [id, dateFilter]);

  // Auto-refresh a cada 30 segundos
  useEffect(() => {
    const interval = setInterval(() => {
      loadData();
      loadBarbers();
      loadWhatsappStatus();
      setLastUpdate(new Date());
    }, 30000);

    return () => clearInterval(interval);
  }, [id, dateFilter]);

  const loadData = async () => {
    try {
      const today = new Date().toISOString().split('T')[0];
      const currentMonth = new Date().toISOString().slice(0, 7);

      // Buscar agendamentos com base no filtro selecionado
      const params = dateFilter === 'hoje'
        ? { data: today }
        : { mes: currentMonth };

      const appointmentsResponse = await getAppointments(id, params);

      setStats(appointmentsResponse.data.estatisticas);

      // Ordenar por data e horário
      const allAppointments = (appointmentsResponse.data.appointments || []).sort((a, b) => {
        if (a.data === b.data) return a.horario.localeCompare(b.horario);
        return a.data.localeCompare(b.data);
      });

      setTodayAppointments(allAppointments);
    } catch (err) {
      console.error('Erro ao carregar dados:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadBarbers = async () => {
    try {
      const response = await api.get(`/barbershops/${id}/barbers`);
      setBarbers(response.data.barbers || []);
    } catch (err) {
      console.error('Erro ao carregar barbeiros:', err);
    }
  };

  const loadWhatsappStatus = async () => {
    try {
      const response = await api.get(`/api/whatsapp/status/${id}`);
      setWhatsappStatus(response.data);
    } catch {
      setWhatsappStatus({ status: 'offline', connected: false, error: 'Worker offline' });
    }
  };

  const loadServicesCount = async () => {
    try {
      const response = await getBarbershopServices(id);
      setServicesCount((response.data.services || []).length);
    } catch {
      setServicesCount(0);
    }
  };

  const handleUpdateStatus = async (appointmentId, newStatus) => {
    setStatusLoading(prev => ({ ...prev, [appointmentId]: true }));
    try {
      await api.put(`/agendamento/${appointmentId}/status`, { status: newStatus });
      toast.success('Status do agendamento atualizado.');
      loadData();
    } catch (err) {
      console.error('Erro ao atualizar status:', err);
      toast.error(err.response?.data?.error || 'Erro ao atualizar status do agendamento');
    } finally {
      setStatusLoading(prev => ({ ...prev, [appointmentId]: false }));
    }
  };

  const handleCancel = async (appointmentId) => {
    if (!confirm('Tem certeza que deseja cancelar este agendamento?')) return;
    setStatusLoading(prev => ({ ...prev, [appointmentId]: true }));
    try {
      await cancelAppointment(appointmentId);
      toast.success('Agendamento cancelado.');
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Erro ao cancelar agendamento');
    } finally {
      setStatusLoading(prev => ({ ...prev, [appointmentId]: false }));
    }
  };

  // Filtrar appointments por barbeiro selecionado
  const filteredTodayAppointments = selectedBarber === 'todos' 
    ? todayAppointments 
    : todayAppointments.filter(apt => apt.barber_id == selectedBarber);

  return (
    <div className="space-y-6" data-tour="dashboard-home">
      <PageHeader
        eyebrow="Visao geral"
        title={barbershop?.nome || 'Seu painel'}
        description="Acompanhe agenda, faturamento, notificacoes e operacao diaria em tempo real."
        icon={LayoutDashboard}
      />

      <OnboardingChecklist
        barbershopId={id}
        onRestartTutorial={onRestartTutorial || (() => tutorial.restartTutorial(id))}
        status={{
          whatsappConnected: whatsappStatus?.connected === true || whatsappStatus?.status === 'connected',
          barbersCount: barbers.length,
          servicesCount,
          appointmentsCount: stats?.total || todayAppointments.length,
          publicUrl: barbershop?.slug ? `/b/${barbershop.slug}` : null
        }}
      />

      {whatsappStatus && whatsappStatus.connected !== true && (
        <div className="rounded-2xl border border-yellow-500/20 bg-yellow-500/10 p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="flex items-start gap-3">
            <MessageCircle className="w-5 h-5 text-yellow-300 mt-0.5" />
            <div>
              <p className="font-semibold text-yellow-100">WhatsApp desconectado</p>
              <p className="text-sm text-yellow-200/80">
                Status: {whatsappStatus.status || 'offline'} {whatsappStatus.error ? `· ${whatsappStatus.error}` : ''}
              </p>
            </div>
          </div>
          <Link to={`/dashboard/${id}/whatsapp`} className="btn-primary py-2 px-4">
            Reconectar
          </Link>
        </div>
      )}

      {/* Stats Grid */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {loading ? (
          <>
            <SkeletonStatsCard />
            <SkeletonStatsCard />
            <SkeletonStatsCard />
            <SkeletonStatsCard />
          </>
        ) : (
          <>
            {[
              { 
                title: 'Hoje', 
                value: stats?.total || 0, 
                icon: Calendar, 
                color: 'text-blue-400', 
                bg: 'bg-blue-500/10' 
              },
              { 
                title: 'Confirmados', 
                value: stats?.confirmados || 0, 
                icon: Users, 
                color: 'text-green-400', 
                bg: 'bg-green-500/10' 
              },
              { 
                title: 'Cancelados', 
                value: stats?.cancelados || 0, 
                icon: Clock, 
                color: 'text-red-400', 
                bg: 'bg-red-500/10' 
              },
              { 
                title: 'Faturamento', 
                value: `R$ ${Number(stats?.faturamento || 0).toFixed(2)}`, 
                icon: TrendingUp, 
                color: 'text-yellow-400', 
                bg: 'bg-yellow-500/10' 
              }
            ].map((stat, index) => (
              <div key={index} className="metric-card">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-gray-400 text-xs uppercase tracking-wide mb-2">{stat.title}</p>
                    <p className="text-2xl font-black text-white">{stat.value}</p>
                  </div>
                  <div className={`w-12 h-12 rounded-2xl ${stat.bg} border border-white/10 flex items-center justify-center`}>
                    <stat.icon className={`w-6 h-6 ${stat.color}`} />
                  </div>
                </div>
              </div>
            ))}
          </>
        )}
      </div>

      {/* Today's Appointments */}
      <SectionCard
        title="Agendamentos de hoje"
        description="Fila operacional do dia com acoes rapidas por status."
        icon={Calendar}
        action={(
          <Link to={`/dashboard/${id}/agendamentos`} className="text-sm text-primary-300 hover:text-primary-200 flex items-center gap-1">
            Ver todos
            <ChevronRight className="w-4 h-4" />
          </Link>
        )}
        bodyClassName="pt-0"
        className="scroll-mt-24"
      >
        <div data-tour="appointments-card" className="absolute inset-0 pointer-events-none" />

        {/* Filtro por Barbeiro */}
        <div className="py-4 border-b border-white/10 flex items-center gap-3">
          <Filter className="w-4 h-4 text-gray-500" />
          <span className="text-sm text-gray-400">Filtrar por barbeiro:</span>
          <div className="flex gap-2 overflow-x-auto pb-1">
            <button
              onClick={() => setSelectedBarber('todos')}
              className={cn(
                "px-3 py-1 rounded-lg text-sm font-medium transition-all whitespace-nowrap",
                selectedBarber === 'todos'
                  ? "bg-primary-600 text-white shadow-lg shadow-primary-600/25"
                  : "bg-dark-700 text-gray-400 hover:bg-dark-600"
              )}
            >
              Todos
            </button>
            {barbers.map(barber => (
              <button
                key={barber.id}
                onClick={() => setSelectedBarber(barber.id)}
                className={cn(
                  "px-3 py-1 rounded-lg text-sm font-medium transition-all whitespace-nowrap flex items-center gap-1",
                  selectedBarber === barber.id
                    ? "bg-primary-600 text-white shadow-lg shadow-primary-600/25"
                    : "bg-dark-700 text-gray-400 hover:bg-dark-600"
                )}
              >
                <User className="w-3 h-3" />
                {barber.name}
              </button>
            ))}
          </div>
        </div>

        <div className="pt-6">
          {loading ? (
            <div className="space-y-3">
              {[...Array(5)].map((_, i) => <SkeletonAppointmentCard key={i} />)}
            </div>
          ) : filteredTodayAppointments.length === 0 ? (
            <EmptyState
              icon={Calendar}
              title="Agenda livre por enquanto"
              description={selectedBarber === 'todos'
                ? 'Nenhum agendamento para hoje.'
                : 'Nenhum agendamento para este barbeiro hoje.'}
            />
          ) : (
            <div className="space-y-3">
              {filteredTodayAppointments.map((appointment) => (
                <div key={appointment.id} className="card card-hover flex flex-col gap-4 p-4 sm:flex-row sm:items-center">
                  <div className="w-full sm:w-16 sm:text-center">
                    <p className="text-lg font-semibold text-white">{appointment.horario}</p>
                    <p className="text-xs text-gray-500">{appointment.duracao_minutos}min</p>
                  </div>
                  <div className="w-12 h-12 rounded-2xl bg-dark-700/80 border border-white/10 overflow-hidden flex items-center justify-center flex-shrink-0">
                    {appointment.servico_image_url ? (
                      <img src={getImageUrl(appointment.servico_image_url)} alt={appointment.servico_nome || 'Serviço'} className="w-full h-full object-cover" />
                    ) : (
                      <Scissors className="w-5 h-5 text-gray-500" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 min-w-0">
                      <p className="font-medium text-white truncate">{appointment.nome_cliente}</p>
                      {appointment.appointment_code && (
                        <span className="px-2 py-0.5 rounded-md bg-purple-500/15 text-purple-300 border border-purple-500/20 text-xs font-semibold flex-shrink-0">
                          #{appointment.appointment_code}
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-gray-400">{appointment.servico_nome || 'Serviço não especificado'}</p>
                    {appointment.deposit_required && (
                      <p className="text-xs text-yellow-300 mt-1">
                        Sinal: R$ {Number(appointment.deposit_amount || appointment.payment_amount || 0).toFixed(2)} · {appointment.payment_status === 'pago' ? 'pago' : appointment.payment_status === 'expired' ? 'expirado' : 'aguardando'}
                      </p>
                    )}
                    {!appointment.deposit_required && appointment.payment_method === 'online' && appointment.payment_status === 'pago' && (
                      <p className="text-xs text-green-300 mt-1">
                        Pago online: R$ {Number(appointment.payment_amount || 0).toFixed(2)}
                      </p>
                    )}
                    {barbers.length > 0 && (
                      <p className="text-xs text-gray-500 mt-1">
                        Barbeiro: {barbers.find(b => b.id == appointment.barber_id)?.name || 'Sem barbeiro'}
                      </p>
                    )}
                    {appointment.data && (
                      <p className="text-xs text-gray-500 mt-1">
                        Data do agendamento: {formatDateDisplay(appointment.data)}
                      </p>
                    )}
                  </div>
                  <span className={`badge ${statusColors[appointment.status]}`}>
                    {statusLabels[appointment.status]}
                  </span>
                  
                  <div className="flex flex-wrap items-center gap-2">
                    {/* Botão Cancelar - visível para todos os status não-terminais */}
                    {isNonTerminalStatus(appointment.status) && (
                      <button
                        onClick={() => handleCancel(appointment.id)}
                        disabled={statusLoading[appointment.id]}
                        className="btn-ghost text-sm text-red-400 hover:text-red-300 min-w-[70px]"
                        title="Cancelar"
                      >
                        {statusLoading[appointment.id] ? '...' : 'Cancelar'}
                      </button>
                    )}

                    {/* Botão Iniciar - para agendado, confirmado, cliente_chegou */}
                    {['agendado', 'confirmado', 'cliente_chegou'].includes(appointment.status) && (
                      <button
                        onClick={() => handleUpdateStatus(appointment.id, 'em_atendimento')}
                        disabled={statusLoading[appointment.id]}
                        className="btn-ghost text-sm text-blue-400 hover:text-blue-300 min-w-[70px]"
                        title="Iniciar Atendimento"
                      >
                        {statusLoading[appointment.id] ? '...' : 'Iniciar'}
                      </button>
                    )}
                    
                    {/* Botão Concluir - para em_atendimento */}
                    {appointment.status === 'em_atendimento' && (
                      <button
                        onClick={() => handleUpdateStatus(appointment.id, 'concluido')}
                        disabled={statusLoading[appointment.id]}
                        className="btn-ghost text-sm text-green-400 hover:text-green-300 min-w-[70px]"
                        title="Concluir Atendimento"
                      >
                        {statusLoading[appointment.id] ? '...' : 'Concluir'}
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </SectionCard>
    </div>
  );
}

function AppointmentsList() {
  const { id } = useParams();
  const toast = useToast();
  const [appointments, setAppointments] = useState([]);
  const [barbers, setBarbers] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [selectedBarber, setSelectedBarber] = useState('todos');
  const [viewMode, setViewMode] = useState('dia');
  const [statusFilter, setStatusFilter] = useState('todos');
  const [loading, setLoading] = useState(true);
  const [statusLoading, setStatusLoading] = useState({});
  const [lastUpdate, setLastUpdate] = useState(new Date());

  useEffect(() => {
    loadAppointments();
    loadBarbers();
  }, [id, selectedDate, viewMode]);

  // Auto-refresh a cada 30 segundos
  useEffect(() => {
    const interval = setInterval(() => {
      loadAppointments();
      loadBarbers();
      setLastUpdate(new Date());
    }, 30000);

    return () => clearInterval(interval);
  }, [id, selectedDate, viewMode]);

  const loadBarbers = async () => {
    try {
      const response = await api.get(`/barbershops/${id}/barbers`);
      setBarbers(response.data.barbers || []);
    } catch (err) {
      console.error('Erro ao carregar barbeiros:', err);
    }
  };

  const loadAppointments = async () => {
    setLoading(true);
    try {
      const params = viewMode === 'dia'
        ? { data: selectedDate }
        : { mes: selectedDate.slice(0, 7) };
      const response = await getAppointments(id, params);
      setAppointments(response.data.appointments);
    } catch (err) {
      console.error('Erro ao carregar agendamentos:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateStatus = async (appointmentId, newStatus) => {
    setStatusLoading(prev => ({ ...prev, [appointmentId]: true }));
    try {
      await api.put(`/agendamento/${appointmentId}/status`, { status: newStatus });
      toast.success('Status do agendamento atualizado.');
      loadAppointments();
    } catch (err) {
      console.error('Erro ao atualizar status:', err);
      toast.error(err.response?.data?.error || 'Erro ao atualizar status do agendamento');
    } finally {
      setStatusLoading(prev => ({ ...prev, [appointmentId]: false }));
    }
  };

  const handleCancel = async (appointmentId) => {
    if (!confirm('Tem certeza que deseja cancelar este agendamento?')) return;
    setStatusLoading(prev => ({ ...prev, [appointmentId]: true }));
    try {
      await cancelAppointment(appointmentId);
      toast.success('Agendamento cancelado.');
      loadAppointments();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Erro ao cancelar agendamento');
    } finally {
      setStatusLoading(prev => ({ ...prev, [appointmentId]: false }));
    }
  };

  // Filtrar appointments por barbeiro selecionado
  const filteredAppointments = selectedBarber === 'todos' 
    ? appointments 
    : appointments.filter(apt => apt.barber_id == selectedBarber);

  const selectedDateObj = new Date(`${selectedDate}T00:00:00`);
  const weekStart = new Date(selectedDateObj);
  weekStart.setDate(selectedDateObj.getDate() - selectedDateObj.getDay());
  const weekEnd = new Date(weekStart);
  weekEnd.setDate(weekStart.getDate() + 6);

  const visibleAppointments = filteredAppointments.filter(apt => {
    const matchesStatus = statusFilter === 'todos' || apt.status === statusFilter;
    if (!matchesStatus) return false;
    if (viewMode === 'dia') return apt.data === selectedDate;
    if (viewMode === 'semana') {
      const aptDate = new Date(`${apt.data}T00:00:00`);
      return aptDate >= weekStart && aptDate <= weekEnd;
    }
    return apt.data?.startsWith(selectedDate.slice(0, 7));
  });

  // Agrupar por barbeiro para modo "Todos"
  const appointmentsByBarber = {};
  visibleAppointments.forEach(appointment => {
    const barberName = barbers.find(b => b.id == appointment.barber_id)?.name || 'Sem Barbeiro';
    if (!appointmentsByBarber[barberName]) {
      appointmentsByBarber[barberName] = [];
    }
    appointmentsByBarber[barberName].push(appointment);
  });

  const generateDates = () => {
    const dates = [];
    const today = new Date();
    for (let i = -3; i <= 10; i++) {
      const date = new Date(today);
      date.setDate(today.getDate() + i);
      dates.push(date.toISOString().split('T')[0]);
    }
    return dates;
  };

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="Agenda"
        title="Agenda operacional"
        description="Veja o dia, semana ou mes, filtre por barbeiro e acompanhe cada atendimento."
        icon={Calendar}
      />

      {/* Filters */}
      <div className="card p-4 sm:p-5">
        <div className="flex flex-col sm:flex-row gap-4">
          <div>
            <label className="label text-sm mb-2">Visão</label>
            <div className="flex rounded-2xl border border-white/10 bg-white/5 p-1">
              {[
                ['dia', 'Dia'],
                ['semana', 'Semana'],
                ['mes', 'Mês']
              ].map(([value, label]) => (
                <button
                  key={value}
                  onClick={() => setViewMode(value)}
                  className={cn('px-3 py-2 rounded-lg text-sm font-medium', viewMode === value ? 'bg-primary-600 text-white' : 'text-gray-400 hover:text-white')}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
          {/* Date Selector */}
          <div className="flex-1">
            <label className="label text-sm mb-2">Data</label>
            <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
              {generateDates().map((date) => (
                <button
                  key={date}
                  onClick={() => setSelectedDate(date)}
                  className={cn(
                    "flex-shrink-0 px-4 py-2 rounded-xl text-sm font-medium transition-all",
                    selectedDate === date 
                      ? "bg-gradient-to-r from-primary-600 to-blue-600 text-white shadow-lg shadow-primary-600/20" 
                      : "bg-white/5 text-gray-400 hover:bg-white/10 hover:text-white"
                  )}
                >
                  {formatDateDisplay(date)}
                </button>
              ))}
            </div>
          </div>
          <div className="sm:w-48">
            <label className="label text-sm mb-2">Status</label>
            <select className="input" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
              <option value="todos">Todos</option>
              <option value="agendado">Agendado</option>
              <option value="confirmado">Confirmado</option>
              <option value="em_atendimento">Em atendimento</option>
              <option value="concluido">Concluído</option>
              <option value="cancelado">Cancelado</option>
              <option value="nao_compareceu">Não compareceu</option>
            </select>
          </div>
        </div>

        {/* Barber Tabs */}
        <div className="mt-4 flex items-center gap-2 overflow-x-auto pb-2">
          <button
            onClick={() => setSelectedBarber('todos')}
            className={cn(
              "px-3 py-1.5 rounded-lg text-sm font-medium transition-all whitespace-nowrap flex items-center gap-1",
              selectedBarber === 'todos'
                ? "bg-gradient-to-r from-primary-600 to-blue-600 text-white shadow-lg shadow-primary-600/20"
                : "bg-white/5 text-gray-400 hover:bg-white/10"
            )}
          >
            <Users className="w-3 h-3" />
            Todos
          </button>
          {barbers.map(barber => (
            <button
              key={barber.id}
              onClick={() => setSelectedBarber(barber.id)}
              className={cn(
                "px-3 py-1.5 rounded-lg text-sm font-medium transition-all whitespace-nowrap flex items-center gap-1",
                selectedBarber === barber.id
                  ? "bg-gradient-to-r from-primary-600 to-blue-600 text-white shadow-lg shadow-primary-600/20"
                  : "bg-white/5 text-gray-400 hover:bg-white/10"
              )}
            >
              <User className="w-3 h-3" />
              {barber.name}
            </button>
          ))}
        </div>
      </div>

      {/* Appointments List */}
      <SectionCard
        title={selectedBarber === 'todos'
          ? `Agendamentos - ${formatDateDisplay(selectedDate)}`
          : `Agendamentos - ${barbers.find(b => b.id == selectedBarber)?.name || 'Barbeiro'}`}
        description={`${visibleAppointments.length} agendamento${visibleAppointments.length !== 1 ? 's' : ''} no filtro atual.`}
        icon={Calendar}
        bodyClassName="space-y-4"
      >
        <div>
          {loading ? (
            <LoadingState />
          ) : visibleAppointments.length === 0 ? (
            <EmptyState
              icon={Calendar}
              title="Nenhum agendamento encontrado"
              description={selectedBarber === 'todos'
                ? 'Nao existe agendamento para esta data.'
                : 'Nao existe agendamento para o barbeiro selecionado nesta data.'}
            />
          ) : selectedBarber === 'todos' ? (
            // Modo "Todos" - Agrupado por barbeiro
            <div className="space-y-4">
              {Object.entries(appointmentsByBarber).map(([barberName, apps]) => (
                <div key={barberName} className="card p-4 bg-dark-700/50 border border-dark-600">
                  <div className="flex items-center space-x-3 mb-3">
                    <Users className="w-4 h-4 text-purple-400" />
                    <h3 className="font-semibold text-white text-lg">{barberName}</h3>
                    <span className="badge bg-purple-500/20 text-purple-400">
                      {apps.length} agendamento{apps.length !== 1 ? 's' : ''}
                    </span>
                  </div>
                  <div className="space-y-2">
                    {apps.map((appointment) => (
                      <div key={appointment.id} className="card card-hover p-4 bg-dark-800/50 border border-dark-600">
                        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                          <div className="w-14 text-center">
                            <p className="text-lg font-bold text-white">{appointment.horario}</p>
                            <p className="text-xs text-gray-500">{appointment.duracao_minutos}min</p>
                          </div>
                          <div className="w-12 h-12 rounded-lg bg-dark-700 overflow-hidden flex items-center justify-center flex-shrink-0">
                            {appointment.servico_image_url ? (
                              <img src={getImageUrl(appointment.servico_image_url)} alt={appointment.servico_nome || 'Serviço'} className="w-full h-full object-cover" />
                            ) : (
                              <Scissors className="w-5 h-5 text-gray-500" />
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-3 mb-1">
                              <h3 className="font-semibold text-white">{appointment.nome_cliente}</h3>
                              {appointment.appointment_code && (
                                <span className="px-2 py-0.5 rounded-md bg-purple-500/15 text-purple-300 border border-purple-500/20 text-xs font-semibold">
                                  #{appointment.appointment_code}
                                </span>
                              )}
                              {appointment.notificado_whatsapp && (
                                <span className="badge badge-success text-xs">WhatsApp ✓</span>
                              )}
                            </div>
                            <p className="text-sm text-gray-400">{appointment.servico_nome}</p>
                            {appointment.deposit_required && (
                              <p className="text-xs text-yellow-300 mt-1">
                                Sinal: R$ {Number(appointment.deposit_amount || appointment.payment_amount || 0).toFixed(2)} · {appointment.payment_status === 'pago' ? 'pago' : appointment.payment_status === 'expired' ? 'expirado' : 'aguardando'}
                              </p>
                            )}
                            {!appointment.deposit_required && appointment.payment_method === 'online' && appointment.payment_status === 'pago' && (
                              <p className="text-xs text-green-300 mt-1">
                                Pago online: R$ {Number(appointment.payment_amount || 0).toFixed(2)}
                              </p>
                            )}
                            <p className="text-sm text-gray-500 mt-1">{appointment.telefone}</p>
                          </div>
                          <div className="flex items-center gap-3">
                            <span className={`badge ${statusColors[appointment.status]}`}>
                              {statusLabels[appointment.status]}
                            </span>
                          </div>
                          <div className="flex items-center gap-1">
                            {/* Cancelar - visível para todos os status não-terminais */}
                            {isNonTerminalStatus(appointment.status) && (
                              <button
                                onClick={() => handleCancel(appointment.id)}
                                disabled={statusLoading[appointment.id]}
                                className="btn-ghost text-sm text-red-400 hover:text-red-300 min-w-[70px]"
                                title="Cancelar"
                              >
                                {statusLoading[appointment.id] ? '...' : 'Cancelar'}
                              </button>
                            )}

                            {/* Iniciar - para agendado, confirmado, cliente_chegou */}
                            {['agendado', 'confirmado', 'cliente_chegou'].includes(appointment.status) && (
                              <button
                                onClick={() => handleUpdateStatus(appointment.id, 'em_atendimento')}
                                disabled={statusLoading[appointment.id]}
                                className="btn-ghost text-sm text-blue-400 hover:text-blue-300 min-w-[70px]"
                                title="Iniciar Atendimento"
                              >
                                {statusLoading[appointment.id] ? '...' : 'Iniciar'}
                              </button>
                            )}
                            
                            {/* Concluir - para em_atendimento */}
                            {appointment.status === 'em_atendimento' && (
                              <button
                                onClick={() => handleUpdateStatus(appointment.id, 'concluido')}
                                disabled={statusLoading[appointment.id]}
                                className="btn-ghost text-sm text-green-400 hover:text-green-300 min-w-[70px]"
                                title="Concluir Atendimento"
                              >
                                {statusLoading[appointment.id] ? '...' : 'Concluir'}
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            // Modo barbeiro específico - lista padrão
            <div className="space-y-4">
              {visibleAppointments.map((appointment) => (
                <div key={appointment.id} className="card card-hover p-5">
                  <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                    <div className="flex items-center gap-4">
                      <div className="w-16 text-center">
                        <p className="text-xl font-bold text-white">{appointment.horario}</p>
                        <p className="text-xs text-gray-500">{appointment.duracao_minutos}min</p>
                      </div>
                      <div className="w-px h-12 bg-dark-600 hidden sm:block" />
                    </div>

                    <div className="flex-1">
                      <div className="flex items-start gap-3">
                        <div className="w-12 h-12 rounded-lg bg-dark-700 overflow-hidden flex items-center justify-center flex-shrink-0">
                          {appointment.servico_image_url ? (
                            <img src={getImageUrl(appointment.servico_image_url)} alt={appointment.servico_nome || 'Serviço'} className="w-full h-full object-cover" />
                          ) : (
                            <Scissors className="w-5 h-5 text-gray-500" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 mb-1">
                        <h3 className="font-semibold text-white">{appointment.nome_cliente}</h3>
                        {appointment.appointment_code && (
                          <span className="px-2 py-0.5 rounded-md bg-purple-500/15 text-purple-300 border border-purple-500/20 text-xs font-semibold">
                            #{appointment.appointment_code}
                          </span>
                        )}
                        {appointment.notificado_whatsapp && (
                          <span className="badge badge-success text-xs">WhatsApp ✓</span>
                        )}
                      </div>
                      <p className="text-sm text-gray-400">{appointment.servico_nome}</p>
                      {appointment.deposit_required && (
                        <p className="text-xs text-yellow-300 mt-1">
                          Sinal: R$ {Number(appointment.deposit_amount || appointment.payment_amount || 0).toFixed(2)} · {appointment.payment_status === 'pago' ? 'pago' : appointment.payment_status === 'expired' ? 'expirado' : 'aguardando'}
                        </p>
                      )}
                      {!appointment.deposit_required && appointment.payment_method === 'online' && appointment.payment_status === 'pago' && (
                        <p className="text-xs text-green-300 mt-1">
                          Pago online: R$ {Number(appointment.payment_amount || 0).toFixed(2)}
                        </p>
                      )}
                      <p className="text-sm text-gray-500 mt-1">{appointment.telefone}</p>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-3">
                      <span className={`badge ${statusColors[appointment.status]}`}>
                        {statusLabels[appointment.status]}
                      </span>
                      
                      <div className="flex items-center gap-1">
                        {/* Cancelar - visível para todos os status não-terminais */}
                        {isNonTerminalStatus(appointment.status) && (
                          <button
                            onClick={() => handleCancel(appointment.id)}
                            disabled={statusLoading[appointment.id]}
                            className="btn-ghost text-sm text-red-400 hover:text-red-300 min-w-[70px]"
                            title="Cancelar"
                          >
                            {statusLoading[appointment.id] ? '...' : 'Cancelar'}
                          </button>
                        )}

                        {/* Iniciar - para agendado, confirmado, cliente_chegou */}
                        {['agendado', 'confirmado', 'cliente_chegou'].includes(appointment.status) && (
                          <button
                            onClick={() => handleUpdateStatus(appointment.id, 'em_atendimento')}
                            disabled={statusLoading[appointment.id]}
                            className="btn-ghost text-sm text-blue-400 hover:text-blue-300 min-w-[70px]"
                            title="Iniciar Atendimento"
                          >
                            {statusLoading[appointment.id] ? '...' : 'Iniciar'}
                          </button>
                        )}
                        
                        {/* Concluir - para em_atendimento */}
                        {appointment.status === 'em_atendimento' && (
                          <button
                            onClick={() => handleUpdateStatus(appointment.id, 'concluido')}
                            disabled={statusLoading[appointment.id]}
                            className="btn-ghost text-sm text-green-400 hover:text-green-300 min-w-[70px]"
                            title="Concluir Atendimento"
                          >
                            {statusLoading[appointment.id] ? '...' : 'Concluir'}
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </SectionCard>
    </div>
  );
}

function ServicesSettings() {
  const { id } = useParams();
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadServices();
  }, [id]);

  const loadServices = async () => {
    try {
      const response = await getBarbershopServices(id);
      setServices(response.data.services);
    } catch (err) {
      console.error('Erro ao carregar serviços:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="card">
      <div className="p-6 border-b border-dark-600">
        <h2 className="text-lg font-semibold text-white flex items-center gap-2">
          <Scissors className="w-5 h-5 text-primary-400" />
          Serviços
        </h2>
      </div>

      <div className="p-6">
        {loading ? (
          <div className="text-center py-8 text-gray-400">Carregando...</div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {services.map((service) => (
              <div key={service.id} className="card card-hover p-4">
                <div className="flex items-start gap-4">
                  <div 
                    className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: `${service.cor}20` }}
                  >
                    <Scissors className="w-6 h-6" style={{ color: service.cor }} />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-medium text-white">{service.nome}</h3>
                    <p className="text-sm text-gray-400">{service.duracao_minutos} minutos</p>
                    <p className="text-lg font-semibold text-primary-400 mt-1">
                      R$ {service.preco.toFixed(2)}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function ScheduleBlocksPage() {
  const { id } = useParams();
  const toast = useToast();
  const [blocks, setBlocks] = useState([]);
  const [barbers, setBarbers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({
    title: 'Bloqueio de horario',
    reason: '',
    date: formatDate(new Date(), 'yyyy-MM-dd'),
    start_time: '12:00',
    end_time: '13:00',
    barber_id: '',
    all_day: false
  });

  const loadBlocks = async () => {
    setLoading(true);
    try {
      const month = form.date ? form.date.slice(0, 7) : formatDate(new Date(), 'yyyy-MM-dd').slice(0, 7);
      const [blocksRes, barbersRes] = await Promise.all([
        api.get(`/barbershops/${id}/blocks`, { params: { month } }),
        api.get(`/barbershops/${id}/barbers`)
      ]);
      setBlocks(blocksRes.data.blocks || []);
      setBarbers(barbersRes.data.barbers || []);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Erro ao carregar bloqueios');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadBlocks();
  }, [id]);

  const saveBlock = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/barbershops/${id}/blocks`, form);
      setForm(prev => ({ ...prev, reason: '' }));
      toast.success('Bloqueio criado.');
      await loadBlocks();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Erro ao salvar bloqueio');
    }
  };

  const removeBlock = async (blockId) => {
    if (!confirm('Remover este bloqueio?')) return;
    await api.delete(`/barbershops/${id}/blocks/${blockId}`);
    toast.success('Bloqueio removido.');
    await loadBlocks();
  };

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="Disponibilidade"
        title="Bloqueio de horarios"
        description="Bloqueie almoco, folgas, manutencao ou horarios de viagem. Esses horarios somem da pagina publica."
        icon={Ban}
      />

      <form onSubmit={saveBlock} className="card p-5 sm:p-6 grid md:grid-cols-2 xl:grid-cols-6 gap-4 items-end">
        <div className="xl:col-span-2">
          <label className="label">Título</label>
          <input className="input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
        </div>
        <div>
          <label className="label">Data</label>
          <input type="date" className="input" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
        </div>
        <div>
          <label className="label">Início</label>
          <input type="time" className="input" value={form.start_time} disabled={form.all_day} onChange={(e) => setForm({ ...form, start_time: e.target.value })} />
        </div>
        <div>
          <label className="label">Fim</label>
          <input type="time" className="input" value={form.end_time} disabled={form.all_day} onChange={(e) => setForm({ ...form, end_time: e.target.value })} />
        </div>
        <div>
          <label className="label">Barbeiro</label>
          <select className="input" value={form.barber_id} onChange={(e) => setForm({ ...form, barber_id: e.target.value })}>
            <option value="">Todos</option>
            {barbers.map(barber => <option key={barber.id} value={barber.id}>{barber.name}</option>)}
          </select>
        </div>
        <div className="xl:col-span-5">
          <label className="label">Motivo</label>
          <input className="input" value={form.reason} placeholder="Ex: almoço, folga, evento..." onChange={(e) => setForm({ ...form, reason: e.target.value })} />
        </div>
        <label className="flex items-center gap-2 text-sm text-gray-300">
          <input type="checkbox" checked={form.all_day} onChange={(e) => setForm({ ...form, all_day: e.target.checked })} />
          Dia inteiro
        </label>
        <button className="btn-primary xl:col-span-6">Adicionar bloqueio</button>
      </form>

      <SectionCard
        title="Bloqueios do mes"
        description="Pausas e indisponibilidades registradas para a barbearia."
        icon={Ban}
        action={<button onClick={loadBlocks} className="btn-secondary py-2 px-4">Atualizar</button>}
      >
        {loading ? (
          <LoadingState />
        ) : blocks.length === 0 ? (
          <EmptyState icon={Ban} title="Nenhum bloqueio criado" description="Crie bloqueios para almoco, folgas, manutencao ou eventos." />
        ) : (
          <div className="space-y-3">
            {blocks.map(block => (
              <div key={block.id} className="card card-hover p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div>
                  <p className="font-semibold text-white">{block.title}</p>
                  <p className="text-sm text-gray-400">
                    {formatDateDisplay(block.date)} - {block.all_day ? 'Dia inteiro' : `${block.start_time} - ${block.end_time}`} - {block.barber_name || 'Todos os barbeiros'}
                  </p>
                  {block.reason && <p className="text-xs text-gray-500 mt-1">{block.reason}</p>}
                </div>
                <button onClick={() => removeBlock(block.id)} className="btn-outline py-2 px-4 border-red-500/30 text-red-300 hover:text-red-200">
                  Remover
                </button>
              </div>
            ))}
          </div>
        )}
      </SectionCard>
    </div>
  );
}

// Main Dashboard Component
export default function Dashboard() {
  const { id } = useParams();
  const navigate = useNavigate();
  const tutorial = useTutorial();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [barbershopName, setBarbershopName] = useState('');
  const [billingWarning, setBillingWarning] = useState(null);
  const [barbershop, setBarbershop] = useState(null);

  const [lastUpdate, setLastUpdate] = useState(new Date());

  const handleTutorialStepChange = useCallback((step) => {
    if (typeof window === 'undefined') return;
    const isMobile = window.matchMedia('(max-width: 1023px)').matches;
    if (!isMobile) return;

    const selector = typeof step?.element === 'string' ? step.element : '';
    const shouldShowSidebar = selector.includes('sidebar') || selector.includes('nav-');
    setSidebarOpen(shouldShowSidebar);
  }, []);

  const handleTutorialFinish = useCallback(() => {
    if (typeof window === 'undefined') return;
    const isMobile = window.matchMedia('(max-width: 1023px)').matches;
    if (isMobile) setSidebarOpen(false);
  }, []);

  const getDashboardTutorialOptions = useCallback(() => ({
    onStepChange: handleTutorialStepChange,
    onFinish: handleTutorialFinish
  }), [handleTutorialFinish, handleTutorialStepChange]);

  const restartDashboardTutorial = useCallback(() => {
    tutorial.restartTutorial(id, getDashboardTutorialOptions());
  }, [getDashboardTutorialOptions, id, tutorial]);

  useEffect(() => {
    const name = localStorage.getItem('barbershopName');
    if (name) setBarbershopName(name);
    
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login');
    }
  }, [navigate]);

  // Auto-refresh global a cada 30 segundos
  useEffect(() => {
    const interval = setInterval(() => {
      setLastUpdate(new Date());
    }, 30000);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (id) {
      loadBarbershop();
    }
  }, [id]);

  useEffect(() => {
    if (id && barbershop) {
      tutorial.maybeStartTutorial(id, getDashboardTutorialOptions());
    }
  }, [id, barbershop, getDashboardTutorialOptions, tutorial]);

  const loadBarbershop = async () => {
    try {
      const response = await api.get(`/barbershops/${id}`);
      setBarbershop(response.data);
    } catch (err) {
      console.error('Erro ao carregar barbearia:', err);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('barbershopId');
    localStorage.removeItem('barbershopName');
    localStorage.removeItem('barbershopSlug');
    navigate('/login');
  };

  const navItems = [
    { path: '', label: 'Visao Geral', icon: LayoutDashboard },
    ...(barbershop?.onboarding_completed === false
      ? [{ path: 'onboarding', label: 'Comecar', icon: Sparkles }]
      : []),
    { path: 'agendamentos', label: 'Agendamentos', icon: Calendar },
    { path: 'bloqueios', label: 'Bloqueios', icon: Ban },
    { path: 'barbeiros', label: 'Barbeiros', icon: Users },
    { path: 'servicos', label: 'Servicos', icon: Scissors },
    { path: 'planos-clientes', label: 'Planos', icon: CreditCard },
    { path: 'publicacoes', label: 'Publicacoes', icon: ImagePlus },
    { path: 'whatsapp', label: 'WhatsApp', icon: MessageCircle },
    { path: 'mercado-pago', label: 'Mercado Pago', icon: CreditCard },
    { path: 'configuracoes', label: 'Configuracoes', icon: Settings },
  ];

  return (
    <div className="min-h-screen dashboard-shell flex">
      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/70 backdrop-blur-sm z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside data-tour="sidebar" className={cn(
        "dashboard-sidebar fixed lg:static inset-y-0 left-0 z-50 w-72 transition-transform duration-300",
        sidebarOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
      )}>
        <div className="h-full flex flex-col">
          {/* Logo */}
          <div className="p-6 border-b border-white/10">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-primary-500 via-purple-600 to-blue-600 flex items-center justify-center shadow-lg shadow-primary-600/25">
                <Scissors className="w-5 h-5 text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[11px] uppercase tracking-[0.24em] text-primary-300">BarberFlow</p>
                <h1 className="font-semibold text-white truncate">{barbershopName || 'Barbearia'}</h1>
              </div>
            </div>
          </div>

          {/* Nav */}
          <nav className="flex-1 p-4 space-y-1.5 overflow-y-auto">
            {navItems.map((item) => (
              <NavLink
                key={item.path}
                to={item.path ? `/dashboard/${id}/${item.path}` : `/dashboard/${id}`}
                end={item.path === ''}
                onClick={() => setSidebarOpen(false)}
                data-tour={
                  item.path === 'whatsapp' ? 'nav-whatsapp' :
                  item.path === 'barbeiros' ? 'nav-barbers' :
                  item.path === 'servicos' ? 'nav-services' :
                  item.path === 'agendamentos' ? 'nav-appointments' :
                  item.path === 'planos-clientes' ? 'nav-plans' :
                  item.path === 'mercado-pago' ? 'nav-mercado-pago' :
                  item.path === 'configuracoes' ? 'nav-settings' :
                  undefined
                }
                className={({ isActive }) => cn(
                  "group flex items-center gap-3 px-4 py-3 rounded-2xl text-sm font-medium transition-all",
                  isActive 
                    ? "bg-gradient-to-r from-primary-600/25 to-blue-600/15 text-white border border-primary-400/30 shadow-lg shadow-primary-600/10"
                    : "text-gray-400 hover:bg-white/5 hover:text-white border border-transparent"
                )}
              >
                <item.icon className="w-5 h-5 transition-transform group-hover:scale-110" />
                {item.label}
              </NavLink>
            ))}
          </nav>

          {/* Footer */}
          <div className="p-4 border-t border-white/10">
            <button
              onClick={handleLogout}
              className="flex items-center gap-3 px-4 py-3 w-full rounded-2xl text-sm font-medium text-gray-400 hover:bg-red-500/10 hover:text-red-300 transition-all border border-transparent hover:border-red-500/20"
            >
              <LogOut className="w-5 h-5" />
              Sair
            </button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 min-w-0">
        {/* Header */}
        <header className="sticky top-0 z-30 glass">
          <div className="flex items-center justify-between px-4 sm:px-6 py-4">
            <div className="flex items-center gap-4">
              <button
                onClick={() => setSidebarOpen(true)}
                className="lg:hidden w-10 h-10 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center text-gray-300"
              >
                <Menu className="w-5 h-5" />
              </button>
              
              <div>
                <p className="text-[11px] uppercase tracking-[0.22em] text-gray-500">Painel operacional</p>
                <h2 className="text-xl font-black text-white">Dashboard</h2>
                <p className="text-xs text-gray-500">Atualizado: {lastUpdate.toLocaleTimeString('pt-BR')}</p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <button
                type="button"
                data-tour="tutorial-button"
                onClick={restartDashboardTutorial}
                className="btn-secondary inline-flex text-sm px-3 py-2"
              >
                <Sparkles className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">Tutorial</span>
              </button>
              {barbershop?.slug ? (
                <Link
                  to={`/b/${barbershop.slug}`}
                  target="_blank"
                  data-tour="public-page-button"
                  className="btn-secondary text-sm px-3 py-2 sm:px-4"
                >
                  <User className="w-4 h-4 mr-2" />
                  <span className="hidden sm:inline">Ver Página</span>
                  <span className="sm:hidden">Página</span>
                </Link>
              ) : (
                <span className="text-sm text-gray-500 px-3 py-2">
                  Carregando...
                </span>
              )}
            </div>
          </div>

          {/* Billing Warning */}
          {billingWarning && (
            <div className="px-4 sm:px-6 pb-4">
              <div className="p-3 rounded-xl bg-yellow-500/10 border border-yellow-500/20 flex items-center gap-3">
                <Bell className="w-5 h-5 text-yellow-400 flex-shrink-0" />
                <p className="text-sm text-yellow-400">
                  Seu plano expira em {billingWarning} dias. <Link to="/billing" className="underline">Renovar</Link>
                </p>
              </div>
            </div>
          )}
        </header>

        {/* Page Content */}
        <div className="p-4 sm:p-6 xl:p-8">
          <div className="mx-auto max-w-7xl">
          <Routes>
            <Route path="/" element={<DashboardHome barbershop={barbershop} setBarbershopName={setBarbershopName} onRestartTutorial={restartDashboardTutorial} />} />
            <Route path="onboarding" element={<OnboardingPage onCompleted={() => setBarbershop(prev => ({ ...prev, onboarding_completed: true }))} />} />
            <Route path="agendamentos" element={<AppointmentsList />} />
            <Route path="bloqueios" element={<ScheduleBlocksPage />} />
            <Route path="barbeiros" element={<BarbersManager />} />
            <Route path="servicos" element={<ServicesManager />} />
            <Route path="planos-clientes" element={<CustomerPlansManager />} />
            <Route path="publicacoes" element={<StoriesManager />} />
            <Route path="whatsapp" element={<WhatsAppConfig />} />
            <Route path="mercado-pago" element={<MercadoPagoConfig />} />
            <Route path="configuracoes" element={<SettingsPage />} />
          </Routes>
          </div>
        </div>
      </main>
    </div>
  );
}
